/**
 * The fair-value band, drawn inside the chart as a TradingView custom study.
 *
 * ## Why a band and not just candles
 *
 * Candles answer "what printed". For a collection of non-identical items that
 * is a harder number to read than it looks: in a quiet hour one sale sets
 * open, high, low and close at once, and whether that sale was a floor piece
 * or a grail decides the whole bar. Scroll a year of that and you get a chart
 * made of disconnected marks with gaps between them — which is exactly the
 * complaint, and it is not a rendering bug. It is what a sparse tape looks
 * like when you draw every print at full weight.
 *
 * The band is the same tape read as a distribution instead of as a sequence.
 * `p10` is the floor proxy, `p90` is where the strong pieces trade, and
 * `fair` sits near the cheap end but participates in the upside — what a
 * typical holder could actually realise, which is closer to the floor than to
 * the median but is not the floor, because a collection whose whole
 * distribution re-rates has genuinely repriced.
 *
 * It is defined on EVERY bucket, including ones with no sale, because a
 * trailing window always has something in it. That is what closes the gaps.
 *
 * ## Why it is drawn over the chart rather than in it
 *
 * The obvious home for this is a TradingView custom study: the library owns
 * the price scale, so a study follows it through zoom and log for free. That
 * was tried, and attaching the study left the chart's panes at their default
 * 300x150 canvas -- the layout pass never ran, so the plot rendered nothing at
 * all. A band is worth having; it is not worth a blank chart, and debugging
 * the library's study metainfo from the outside is not a good use of the time
 * this feature has.
 *
 * The overlay does not have to re-derive the price scale, which was the
 * original objection to it, because the library exposes its own:
 * `IPaneApi.getMainSourcePriceScale().priceToCoordinate()`. So the band is
 * positioned by the same mapping the candles are, through a documented API,
 * and it follows zoom and log scale exactly as a study would. It reuses the
 * `ChartGeometry` the outlier dots already ride on.
 *
 * ## How the values get in
 *
 * The datafeed writes each window's band into the module store below, keyed by
 * symbol; the overlay reads it back. The store is the seam, and it is what
 * stops anything having to invent a second request for data that arrived with
 * the bars.
 */

import type { ChartTrendPoint } from './datafeed'

/**
 * Bands by symbol, merged by bucket time.
 *
 * Merged rather than replaced because TradingView fetches history in chunks
 * as the user scrolls back, and each chunk carries only its own window. A
 * replace would erase the band behind the viewport every time someone panned.
 */
const bands = new Map<string, Map<number, ChartTrendPoint>>()

/** Bumped on every write, so a study knows its inputs moved. */
let revision = 0

export function putTrend(symbol: string, points: ChartTrendPoint[]): void {
	if (points.length === 0) {
		return
	}
	let byTime = bands.get(symbol)
	if (!byTime) {
		byTime = new Map()
		bands.set(symbol, byTime)
	}
	for (const p of points) {
		byTime.set(p.time, p)
	}
	revision += 1
}

/**
 * Forget a symbol's band.
 *
 * Called when a chart switches symbols. Without it the store would grow for
 * the life of the page, and — worse — a denomination switch would leave the
 * previous denomination's numbers sitting under the new symbol's key if the
 * two ever collided.
 */
export function clearTrend(symbol: string): void {
	if (bands.delete(symbol)) {
		revision += 1
	}
}

export function trendRevision(): number {
	return revision
}

export function hasTrend(symbol: string): boolean {
	const b = bands.get(symbol)
	return b !== undefined && b.size > 0
}

/**
 * A symbol's band, oldest first.
 *
 * Sorted on read rather than on write: writes happen once per history page and
 * reads once per repaint, but a repaint is cheap only if it is not also a
 * sort, so the sorted copy is memoised against the store's revision.
 */
let sortedCache: { symbol: string; rev: number; points: ChartTrendPoint[] } | null = null

export function trendPoints(symbol: string): ChartTrendPoint[] {
	if (sortedCache && sortedCache.symbol === symbol && sortedCache.rev === revision) {
		return sortedCache.points
	}
	const byTime = bands.get(symbol)
	const points = byTime ? [...byTime.values()].sort((a, b) => a.time - b.time) : []
	sortedCache = { symbol, rev: revision, points }
	return points
}
